import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

/* any functionality that's shared between component variants but not needed by external components goes in this file */
import { useState, useRef, useEffect } from 'react';
import { getFirstTabbableDescendent, getAllTabbableDescendents, useLayoutEffectIsomorphic } from '@chegg-ui/utils';

var excludeElement = function excludeElement(elem) {
  return elem.getAttribute('aria-hidden') === 'true' || elem.getAttribute('inert') === 'true' || elem.tagName === 'SCRIPT' || elem.tagName === 'STYLE';
};

var makeBodyElementsInert = function makeBodyElementsInert(containerElementId) {
  var containerElementAncestors = [];
  /* istanbul ignore next */

  var containerElement = document.getElementById("".concat(containerElementId, "-container")) || document.getElementById(containerElementId);
  /*
    ToDo - jest always return containerElementAncestor document.body and this makes testing below logic.
    We will remove below ignores once found some solution for this jest issue.
  */

  var containerElementAncestor = containerElement.parentElement;
  /* istanbul ignore next */

  while (containerElementAncestor) {
    if (containerElementAncestor === document.body) {
      break;
    }

    containerElementAncestors.push(containerElementAncestor);
    containerElementAncestor = containerElementAncestor.parentElement;
  }

  var elementsToHide = [];
  var bodyChilds = Array.from(containerElementAncestor.children);
  bodyChilds.forEach(function (elem) {
    if (excludeElement(elem) || elem === containerElement || elem === containerElementAncestors[containerElementAncestors.length - 1]) {
      return;
    }

    elementsToHide.push(elem);
  });
  /* istanbul ignore next */

  if (containerElementAncestors.length) {
    containerElementAncestors.forEach(function (root, index) {
      Array.from(root.children).forEach(function (elem) {
        if (excludeElement(elem) || index === 0 && elem === containerElement || index !== 0 && elem === containerElementAncestors[index - 1]) {
          return;
        }

        elementsToHide.push(elem);
      });
    });
  }

  for (var i = 0; i < elementsToHide.length; i++) {
    var element = elementsToHide[i];
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('inert', 'true');
  }

  return elementsToHide;
};

var resetInertBodyElements = function resetInertBodyElements(elementsMadeInert) {
  for (var i = 0; i < elementsMadeInert.length; i++) {
    var element = elementsMadeInert[i];
    element.removeAttribute('aria-hidden');
    element.removeAttribute('inert');
  }
};

export function useModalBehavior(_ref) {
  var isOpen = _ref.isOpen,
      onClose = _ref.onClose,
      id = _ref.id,
      _ref$focusFirstActive = _ref.focusFirstActiveElement,
      focusFirstActiveElement = _ref$focusFirstActive === void 0 ? true : _ref$focusFirstActive;
  var modalRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      closing = _useState2[0],
      setClosing = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      mounted = _useState4[0],
      setMounted = _useState4[1];

  var _useState5 = useState([]),
      _useState6 = _slicedToArray(_useState5, 2),
      elementsMadeInert = _useState6[0],
      setElementsMadeInert = _useState6[1];

  var returnFocus = useRef(null); // all the logic contained within is client-side only in response to user action, thus safe for SSR

  useLayoutEffectIsomorphic(function () {
    if (isOpen && !mounted) {
      returnFocus.current = document.activeElement;
      var modalElement = modalRef.current; // we need to make all other elements in the DOM body inert, per legacy ARIA spec
      // https://www.w3.org/TR/wai-aria-practices-1.1/examples/dialog-modal/dialog.html

      setElementsMadeInert(makeBodyElementsInert(id));
      /* istanbul ignore else */

      if (modalElement) {
        if (focusFirstActiveElement) {
          var focusTarget = getFirstTabbableDescendent(modalElement);
          /* istanbul ignore else */

          if (focusTarget) {
            focusTarget.focus();
          }
        } else {
          modalElement.setAttribute('tabIndex', '-1');
          modalElement.focus();
        }
      }

      setMounted(true); // we don't want to run any of the below logic on the initial mount
    } else if (!isOpen && mounted) {
      resetInertBodyElements(elementsMadeInert);

      if (window.matchMedia && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setClosing(true);
        setTimeout(function () {
          setClosing(false);
          setMounted(false);
        }, // important: timeout number must match the fadeout animation time to keep things smooth
        300);
      } else {
        setMounted(false);
      }
    }

    return function () {
      if (mounted) {
        resetInertBodyElements(elementsMadeInert);
        returnFocus.current && returnFocus.current.focus();
      }
    };
  }, [isOpen, mounted]);
  useEffect(function () {
    document.addEventListener('keydown', onModalKeyDown);
    return function () {
      document.removeEventListener('keydown', onModalKeyDown);
    };
  }, [isOpen]);

  var focusFirstTabbable = function focusFirstTabbable(element, event) {
    event && event.preventDefault();
    /* istanbul ignore else */

    if (element) {
      var firstChild = getFirstTabbableDescendent(element);
      /* istanbul ignore else */

      if (firstChild) {
        firstChild.focus();
      }
    }
  };

  var focusLastTabbable = function focusLastTabbable(element, event) {
    event && event.preventDefault();
    /* istanbul ignore else */

    if (element) {
      var allChildren = getAllTabbableDescendents(element);
      /* istanbul ignore else */

      if (allChildren.length) {
        allChildren[allChildren.length - 1].focus();
      }
    }
  };

  var onModalKeyDown = function onModalKeyDown(e) {
    var modalElement = modalRef.current;
    /* istanbul ignore else */

    if (modalElement && isOpen) {
      if (e.key === 'Tab') {
        var activeDescendent;
        var allChildren = getAllTabbableDescendents(modalElement);
        allChildren.forEach(function (element) {
          if (element === e.target) {
            activeDescendent = element;
          }
        });

        if (activeDescendent) {
          if (e.shiftKey) {
            /* istanbul ignore else */
            if (allChildren[0] === e.target) {
              focusLastTabbable(modalRef.current, e);
            }
          } else {
            /* istanbul ignore else */
            if (allChildren[allChildren.length - 1] === e.target) {
              focusFirstTabbable(modalRef.current, e);
            }
          }
        } else {
          if (e.shiftKey) {
            focusLastTabbable(modalRef.current, e);
          } else {
            focusFirstTabbable(modalRef.current, e);
          }
        }
      } else if ((e.key === 'Escape' || e.key === 'Esc') && onClose) {
        e.preventDefault();
        onClose(e);
      }
    }
  };

  return {
    modalRef: modalRef,
    closing: closing,
    mounted: mounted,
    onModalKeyDown: onModalKeyDown
  };
}